/**
 * Socket.IO Configuration
 * 
 * Configures Socket.IO client based on browser capabilities and environment.
 * Implements:
 * - Transport priority (WebSocket → polling)
 * - Browser-specific options
 * - Mobile-optimized parameters
 * 
 * @module socketIOConfig
 * @see Task 11.2: 实现 Socket.IO 配置
 */

import { ManagerOptions, SocketOptions } from 'socket.io-client';
import {
  BrowserCapabilities,
  getBrowserCapabilities,
  getNetworkInfo,
  getRecommendedTransports,
  getRecommendedReconnectDelay,
  shouldUseAggressiveReconnect,
  NetworkInfo
} from './browserDetection';
import { wsLogger } from './logger';

/**
 * Socket.IO configuration options
 */
export interface SocketIOConfig extends Partial<ManagerOptions & SocketOptions> {
  /** Server URL */
  serverUrl: string;
  /** Authentication token */
  authToken: string;
}

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG = {
  // Connection settings
  timeout: 20000,                    // Connection timeout (20s)

  // Reconnection settings
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,           // Initial delay (1s)
  reconnectionDelayMax: 30000,       // Max delay (30s)
  randomizationFactor: 0.5,          // Randomization for backoff

  // Heartbeat settings (Socket.IO uses ping/pong internally)
  // These are handled by Socket.IO engine

  // Transport settings
  transports: ['websocket', 'polling'] as ('websocket' | 'polling')[],
  upgrade: true,                     // Allow transport upgrade
  rememberUpgrade: true,             // Remember successful upgrade

  // Buffer settings
  forceNew: false,
  multiplex: true,
} as const;

/**
 * Mobile-specific configuration overrides
 */
export const MOBILE_CONFIG = {
  // Longer timeout for mobile networks
  timeout: 30000,                    // 30s timeout

  // More conservative reconnection for battery life
  reconnectionAttempts: 3,
  reconnectionDelay: 2000,           // Start with 2s delay
  reconnectionDelayMax: 60000,       // Max 60s delay

  // Prefer polling on unreliable mobile networks
  // (will be adjusted based on network quality)
  upgrade: true,
  rememberUpgrade: true,
} as const;

/**
 * WebView-specific configuration overrides
 */
export const WEBVIEW_CONFIG = {
  // WebViews may have restricted WebSocket support
  transports: ['polling', 'websocket'] as ('websocket' | 'polling')[],

  // Longer timeout for WebView environments
  timeout: 25000,

  // More reconnection attempts for WebView
  reconnectionAttempts: 5,
  reconnectionDelay: 1500,
} as const;

/**
 * Safari-specific configuration overrides
 */
export const SAFARI_CONFIG = {
  // Safari has aggressive connection throttling when tab is inactive
  // Use shorter timeout to detect issues faster
  timeout: 15000,

  // Safari may close WebSocket connections aggressively
  reconnectionDelay: 500,            // Faster initial reconnect
  reconnectionDelayMax: 20000,       // Lower max delay
} as const;

/**
 * iOS-specific configuration overrides
 */
export const IOS_CONFIG = {
  // iOS suspends WebSocket when app goes to background
  // Need faster reconnection when app returns to foreground
  reconnectionDelay: 500,
  reconnectionDelayMax: 15000,

  // iOS Safari has connection limits
  timeout: 20000,
} as const;

/**
 * Slow network configuration overrides
 */
export const SLOW_NETWORK_CONFIG = {
  // Much longer timeout for slow networks
  timeout: 45000,

  // Prefer polling on slow networks (more reliable)
  transports: ['polling', 'websocket'] as ('websocket' | 'polling')[],

  // Conservative reconnection
  reconnectionDelay: 5000,
  reconnectionDelayMax: 120000,      // Up to 2 minutes
  reconnectionAttempts: 10,          // More attempts
} as const;

/**
 * PWA-specific configuration overrides
 */
export const PWA_CONFIG = {
  // PWAs should behave like native apps
  // Use aggressive reconnection
  reconnectionDelay: 500,
  reconnectionDelayMax: 10000,
  reconnectionAttempts: 10,
} as const;

/**
 * Get transport configuration based on browser capabilities
 */
export function getTransportConfig(
  capabilities: BrowserCapabilities,
  networkInfo: NetworkInfo
): Pick<SocketIOConfig, 'transports' | 'upgrade' | 'rememberUpgrade'> {
  const transports = getRecommendedTransports(capabilities);

  // For slow networks, prefer polling
  if (networkInfo.connectionType === 'slow-2g' || networkInfo.connectionType === '2g') {
    return {
      transports: ['polling', 'websocket'],
      upgrade: true,
      rememberUpgrade: false, // Don't remember upgrade on slow networks
    };
  }

  // For WebView, prefer polling initially
  if (capabilities.isWebView) {
    return {
      transports: ['polling', 'websocket'],
      upgrade: true,
      rememberUpgrade: true,
    };
  }

  // Default: prefer WebSocket
  return {
    transports,
    upgrade: true,
    rememberUpgrade: true,
  };
}

/**
 * Get reconnection configuration based on browser and network
 */
export function getReconnectionConfig(
  capabilities: BrowserCapabilities,
  networkInfo: NetworkInfo
): Pick<SocketIOConfig, 'reconnection' | 'reconnectionAttempts' | 'reconnectionDelay' | 'reconnectionDelayMax' | 'randomizationFactor'> {
  const baseDelay = getRecommendedReconnectDelay(networkInfo);
  const aggressive = shouldUseAggressiveReconnect(capabilities, networkInfo);

  // Offline: disable reconnection (will be handled by online event)
  if (!networkInfo.isOnline) {
    return {
      reconnection: false,
      reconnectionAttempts: 0,
      reconnectionDelay: baseDelay,
      reconnectionDelayMax: 60000,
      randomizationFactor: 0.5,
    };
  }

  // Aggressive reconnection for good conditions
  if (aggressive) {
    return {
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: baseDelay,
      reconnectionDelayMax: 15000,
      randomizationFactor: 0.3,
    };
  }

  // Conservative reconnection for mobile/slow networks
  if (capabilities.isMobile || networkInfo.connectionType === '3g') {
    return {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: baseDelay,
      reconnectionDelayMax: 45000,
      randomizationFactor: 0.5,
    };
  }

  // Default reconnection settings
  return {
    reconnection: true,
    reconnectionAttempts: DEFAULT_CONFIG.reconnectionAttempts,
    reconnectionDelay: baseDelay,
    reconnectionDelayMax: DEFAULT_CONFIG.reconnectionDelayMax,
    randomizationFactor: DEFAULT_CONFIG.randomizationFactor,
  };
}

/**
 * Get timeout configuration based on browser and network
 */
export function getTimeoutConfig(
  capabilities: BrowserCapabilities,
  networkInfo: NetworkInfo
): Pick<SocketIOConfig, 'timeout'> {
  // Slow networks need longer timeout
  if (networkInfo.connectionType === 'slow-2g') {
    return { timeout: 60000 }; // 60s
  }

  if (networkInfo.connectionType === '2g') {
    return { timeout: 45000 }; // 45s
  }

  if (networkInfo.connectionType === '3g') {
    return { timeout: 30000 }; // 30s
  }

  // Mobile devices may have variable latency
  if (capabilities.isMobile) {
    return { timeout: 25000 }; // 25s
  }

  // Safari needs shorter timeout due to aggressive throttling
  if (capabilities.browserName === 'safari') {
    return { timeout: 15000 }; // 15s
  }

  // Default timeout
  return { timeout: DEFAULT_CONFIG.timeout };
}

/**
 * Get browser-specific configuration overrides
 */
export function getBrowserSpecificConfig(
  capabilities: BrowserCapabilities
): Partial<SocketIOConfig> {
  const config: Partial<SocketIOConfig> = {};

  // Safari-specific settings
  if (capabilities.browserName === 'safari') {
    Object.assign(config, SAFARI_CONFIG);
  }

  // iOS-specific settings (includes Safari on iOS)
  if (capabilities.isIOS) {
    Object.assign(config, IOS_CONFIG);
  }

  // WebView-specific settings
  if (capabilities.isWebView) {
    Object.assign(config, WEBVIEW_CONFIG);
  }

  // PWA-specific settings
  if (capabilities.isPWA) {
    Object.assign(config, PWA_CONFIG);
  }

  // Mobile-specific settings (if not already covered)
  if (capabilities.isMobile && !capabilities.isIOS && !capabilities.isWebView) {
    Object.assign(config, MOBILE_CONFIG);
  }

  return config;
}

/**
 * Create Socket.IO configuration based on browser capabilities and network
 * 
 * This is the main entry point for getting optimized Socket.IO configuration.
 * It combines:
 * - Default configuration
 * - Browser-specific overrides
 * - Network-aware settings
 * - Transport priority
 * 
 * @param serverUrl - WebSocket server URL
 * @param authToken - Authentication token
 * @param capabilities - Optional browser capabilities (auto-detected if not provided)
 * @param networkInfo - Optional network info (auto-detected if not provided)
 * @returns Optimized Socket.IO configuration
 */
export function createSocketIOConfig(
  serverUrl: string,
  authToken: string,
  capabilities?: BrowserCapabilities,
  networkInfo?: NetworkInfo
): SocketIOConfig {
  // Get browser capabilities and network info
  const browserCapabilities = capabilities || getBrowserCapabilities();
  const network = networkInfo || getNetworkInfo();

  wsLogger.debug('SocketIO Config', 'Creating configuration', {
    browser: browserCapabilities.browserName,
    version: browserCapabilities.browserMajorVersion,
    isMobile: browserCapabilities.isMobile,
    isWebView: browserCapabilities.isWebView,
    isPWA: browserCapabilities.isPWA,
    networkType: network.connectionType,
    isOnline: network.isOnline,
  });

  // Start with default configuration
  const config: SocketIOConfig = {
    serverUrl,
    authToken,
    ...DEFAULT_CONFIG,
  };

  // Apply browser-specific overrides
  const browserConfig = getBrowserSpecificConfig(browserCapabilities);
  Object.assign(config, browserConfig);

  // Apply transport configuration
  const transportConfig = getTransportConfig(browserCapabilities, network);
  Object.assign(config, transportConfig);

  // Apply reconnection configuration
  const reconnectionConfig = getReconnectionConfig(browserCapabilities, network);
  Object.assign(config, reconnectionConfig);

  // Apply timeout configuration
  const timeoutConfig = getTimeoutConfig(browserCapabilities, network);
  Object.assign(config, timeoutConfig);

  // Add authentication
  config.auth = {
    token: authToken,
  };

  wsLogger.debug('SocketIO Config', 'Configuration created', {
    transports: config.transports,
    timeout: config.timeout,
    reconnectionDelay: config.reconnectionDelay,
    reconnectionDelayMax: config.reconnectionDelayMax,
    reconnectionAttempts: config.reconnectionAttempts,
  });

  return config;
}

/**
 * Extract Socket.IO manager options from config
 */
export function extractManagerOptions(config: SocketIOConfig): Partial<ManagerOptions> {
  const {
    serverUrl,
    authToken,
    ...options
  } = config;

  return options as Partial<ManagerOptions>;
}

/**
 * Extract Socket.IO socket options from config
 */
export function extractSocketOptions(config: SocketIOConfig): Partial<SocketOptions> {
  return {
    auth: config.auth,
  };
}

/**
 * Update configuration based on network changes
 * 
 * Call this when network conditions change to get updated configuration.
 */
export function updateConfigForNetwork(
  currentConfig: SocketIOConfig,
  newNetworkInfo: NetworkInfo
): Partial<SocketIOConfig> {
  const capabilities = getBrowserCapabilities();

  wsLogger.debug('SocketIO Config', 'Updating for network change', {
    connectionType: newNetworkInfo.connectionType,
    isOnline: newNetworkInfo.isOnline,
  });

  // Get new transport config
  const transportConfig = getTransportConfig(capabilities, newNetworkInfo);

  // Get new reconnection config
  const reconnectionConfig = getReconnectionConfig(capabilities, newNetworkInfo);

  // Get new timeout config
  const timeoutConfig = getTimeoutConfig(capabilities, newNetworkInfo);

  return {
    ...transportConfig,
    ...reconnectionConfig,
    ...timeoutConfig,
  };
}

/**
 * Check if current configuration should use WebSocket
 */
export function shouldUseWebSocket(config: SocketIOConfig): boolean {
  const transports = config.transports || DEFAULT_CONFIG.transports;
  return transports[0] === 'websocket';
}

/**
 * Get configuration summary for logging/debugging
 */
export function getConfigSummary(config: SocketIOConfig): Record<string, any> {
  return {
    serverUrl: config.serverUrl,
    hasAuth: !!config.auth,
    transports: config.transports,
    timeout: config.timeout,
    reconnection: config.reconnection,
    reconnectionAttempts: config.reconnectionAttempts,
    reconnectionDelay: config.reconnectionDelay,
    reconnectionDelayMax: config.reconnectionDelayMax,
    upgrade: config.upgrade,
    rememberUpgrade: config.rememberUpgrade,
  };
}
